import { JSX, useCallback, useMemo } from 'react';
import type { DroppableProvided } from '@hello-pangea/dnd';
import { Button } from '@heroui/react';
import { Plus } from 'lucide-react';

import { Feedback } from '~/components/Feedback';
import { AddIcon, TaskListIcon } from '~/components/Icons';
import { ScrollArea } from '~/components/ScrollArea';

import type { Subtask, TaskProps, TaskStatus } from '~/types';

import { TaskCard } from '../TaskCard';

interface ProjectsBoardProps {
  title: string;
  status: TaskStatus;
  tasks: TaskProps[] | null;
  onTaskAdd: () => void;
  onTaskSelect: (task: TaskProps) => void;
  onTaskUpdate: (taskId: string, updates: Partial<TaskProps>) => void;
  onTaskDelete: (taskId: string) => void;
  onSubtaskAdd: (taskId: string) => void;
  onSubtaskUpdate: (
    taskId: string,
    subtaskId: string,
    updates: Partial<Omit<Subtask, 'id' | 'taskId' | 'createdAt' | 'updatedAt'>>,
  ) => void;
  onSubtaskDelete: (taskId: string, subtaskId: string) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  droppableProvided: DroppableProvided;
}

export function ProjectsBoardColumn(props: ProjectsBoardProps): JSX.Element {
  const {
    droppableProvided,
    onSubtaskAdd,
    onSubtaskDelete,
    onSubtaskUpdate,
    onTaskAdd,
    onTaskDelete,
    onTaskSelect,
    onTaskUpdate,
    tasks,
    title,
  } = props;

  const handleTaskSelect = useCallback(
    (task: TaskProps) => {
      onTaskSelect(task);
    },
    [onTaskSelect],
  );

  // Pre-create select handlers for each task to avoid arrow functions in JSX
  const taskSelectHandlers = useMemo(() => {
    if (!tasks) {
      return {};
    }

    return tasks.reduce(
      (acc, task) => {
        acc[task.id] = (): void => handleTaskSelect(task);

        return acc;
      },
      {} as Record<string, () => void>,
    );
  }, [tasks, handleTaskSelect]);

  const renderContent = (): JSX.Element => {
    if (tasks === null) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-center p-6">
          <p className="text-muted mb-2">Select a project to view and manage tasks</p>
          <p className="text-sm text-muted">Click on a project from the list on the left</p>
        </div>
      );
    }

    if (tasks.length === 0) {
      return (
        <Feedback
          size="sm"
          icon={<TaskListIcon className="w-12 h-12" />}
          title="No tasks yet"
          description={null}
          type="no-results"
        >
          <Button
            className="font-medium"
            color="primary"
            onPress={onTaskAdd}
            startContent={<AddIcon className="w-4 h-4" />}
          >
            Add Task
          </Button>
        </Feedback>
      );
    }

    return (
      <div className="space-y-2" role="listbox">
        {tasks.map((task, index) => (
          <TaskCard
            key={task.id}
            task={task}
            index={index}
            onSelect={taskSelectHandlers[task.id]}
            onUpdate={onTaskUpdate}
            onDelete={onTaskDelete}
            onSubtaskAdd={onSubtaskAdd}
            onSubtaskUpdate={onSubtaskUpdate}
            onSubtaskDelete={onSubtaskDelete}
            isLoadingSubtasks={false}
            subtasksError={null}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      ref={droppableProvided.innerRef}
      {...droppableProvided.droppableProps}
      className="bg-surface rounded-lg min-h-[200px] min-w-[320px] lg:min-w-0 w-full flex flex-col shadow-sm h-full flex-1 mx-1.5 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <button
          type="button"
          onClick={onTaskAdd}
          className="p-1.5 hover:bg-surface-elevated dark:hover:bg-surface-elevated rounded-full transition-colors duration-200"
          aria-label="Add task"
        >
          <Plus className="h-5 w-5 text-secondary-600 dark:text-muted" />
        </button>
      </div>

      <ScrollArea className="flex-1 overflow-y-auto px-4 pb-4" padding={false}>
        <div className="space-y-2 py-2">{renderContent()}</div>
        {droppableProvided.placeholder}
      </ScrollArea>
    </div>
  );
}
